import { IoSearchOutline } from 'react-icons/io5';
import { FiDownload } from 'react-icons/fi';
import React, { useContext, useEffect, useState } from 'react';
import Button from '../../components/shared/custom-btn';
import Heading from '../../components/shared/heading';
import VehicleTable from './job-table';
import { AccountsContext } from '../../components/context/account-context';
import autoTable from 'jspdf-autotable';
import jsPDF from 'jspdf';
import logo from '../../assets/images/logo.jpg';
import { CSVLink } from 'react-csv';
import Modal from '../../components/shared/modal';
import AddModal from './add-modal';
import { getAllJobs, getJobStatuses } from '../../services/jobs-service';
import { formatUTCTimestampToLocal, getDateRangeUTC } from '../../utils/set-date-range-utils';
import DateRangePicker from '../../components/shared/date-range-picker';
import Help from './help';
import { usePageHelp } from '../../components/context/help-context';

const Jobs = () => {
    usePageHelp(Help);
    const { accountId } = useContext(AccountsContext);
    const [jobData, setJobData] = useState([]);
    const [openAddModal, setOpenAddModal] = useState(false);
    const [newlyAddedItem, setNewlyAddedItem] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [globalDataLoading, setGlobalDataLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [vehicleTypes, setVehicleTypes] = useState([]);
    const [vehicleBodies, setVehicleBodies] = useState([]);
    const [vehicleMakes, setVehicleMakes] = useState([]);
    const [jobStatuses, setJobStatuses] = useState([]);
    const [fetchingGlobalData, setFetchingGlobalData] = useState(false);
    const [dateRange, setDateRange] = useState({
        startDate: new Date(new Date().setDate(new Date().getDate() - 7)),
        endDate: new Date(),
    });
    const [selectedRows, setSelectedRows] = useState({});

    const fetchJobs = async () => {
        setLoading(true);
        try {
            const { from, to } = getDateRangeUTC(dateRange);
            // Fetch both vehicles and statuses in parallel
            const [jobsResponse, statusesResponse] = await Promise.all([
                getAllJobs(accountId),
                getJobStatuses(accountId)
            ]);

            if (jobsResponse.success) {
                const sortedData = jobsResponse.data.data.sort((a, b) => new Date(b.createdOn) - new Date(a.createdOn));
                setJobData(sortedData || []);
                setError(null);
            } else {
                setError(jobsResponse.data.message || 'Failed to fetch jobs records');
            }

            if (statusesResponse.success) {
                setJobStatuses(statusesResponse.data.data || []);
            } else {
                console.error('Failed to fetch job statuses:', statusesResponse.data.message);
            }
        } catch (err) {
            console.error("Error fetching job and statuses:", err);
            setError('An error occurred while fetching job records');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchJobs();
    }, [accountId]);

    const handleOpenAddModal = async () => {
        try {
            setOpenAddModal(true);
            if (vehicleTypes.length === 0 || vehicleBodies.length === 0 || vehicleMakes.length === 0) {
                // await fetchGlobalData();
            }
        } catch (error) {
            console.error("Failed to fetch global data", error);
            setError('Failed to fetch required data for adding a job');
        }
        setOpenAddModal(true);
    };

    const handleCloseAddModal = () => {
        setOpenAddModal(false);
    };

    const handleAddItem = (newItem) => {
        setNewlyAddedItem(newItem);
        fetchJobs();
    };

    const handleOpenEditModal = (item) => {
        setNewlyAddedItem(item);
    };

    const handleClearNewlyAddedItem = () => {
        setNewlyAddedItem(null);
    };

    // Filter data for export based on selected rows
    const exportData = Object.keys(selectedRows).length > 0
        ? jobData.filter((_, index) => selectedRows[index])
        : jobData;

    // CSV export
    const csvData = exportData.map(item => ({
        Name: item.name || '',
        Type: item.type || '',
        Location: item.location || '',
        'Start Time': formatUTCTimestampToLocal(item.startTime, 'yyyy-MM-dd hh:mm a') || '',
        'End Time': formatUTCTimestampToLocal(item.startTime, 'yyyy-MM-dd hh:mm a') || '',
        Note: item.note || '',
        Status: item.status || '',
    }));


    // PDF export
    const handleExportPDF = () => {
        const doc = new jsPDF();

        // Logo placement
        const logoWidth = 25;
        const logoHeight = 15;
        const pageWidth = doc.internal.pageSize.getWidth();
        const logoX = (pageWidth - logoWidth) / 2;
        const logoY = 2;

        doc.addImage(logo, 'PNG', logoX, logoY, logoWidth, logoHeight);

        // Title
        const title = 'Job Report';
        const titleY = logoY + logoHeight + 5;
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        const titleWidth = doc.getTextWidth(title);
        const titleX = (pageWidth - titleWidth) / 2;
        doc.text(title, titleX, titleY);

        // Table
        autoTable(doc, {
            head: [['Name', 'Type', 'Location', 'Start Time', 'End Time', 'Note', 'Status']],
            body: exportData.map(item => [
                item.name || '',
                item.type || '',
                item.location || '',
                formatUTCTimestampToLocal(item.startTime, 'yyyy-MM-dd hh:mm a') || '',
                formatUTCTimestampToLocal(item.endTime, 'yyyy-MM-dd hh:mm a') || '',
                item.note || '',
                item.status || '',
            ]),
            startY: titleY + 5,
        });


        // File name
        const fileName = `jobs-report.pdf`;
        doc.save(fileName);
    };

    const isExportDisabled = Object.keys(selectedRows).length === 0;

    return (
        <div className="px-6 py-4">
            <div className='flex mb-2 items-center justify-between'>
                <div className=''>
                    <Heading>Jobs</Heading>
                </div>
                <Button
                    label='Add Job'
                    onClick={handleOpenAddModal}
                    className='text-white px-4 !py-1 rounded-lg bg-btnBg'
                    disabled={globalDataLoading}
                />
            </div>

            <div className='space-y-4'>
                {error && (
                    <div className="text-red-600 bg-red-100 px-4 py-2 rounded-md">
                        {error}
                    </div>
                )}

                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex gap-3 flex-wrap">
                        <div className="bg-white rounded-md px-3 flex border border-border items-center gap-2 w-44 lg:w-72 py-1">
                            <IoSearchOutline className="text-gray-500 text-lg" />
                            <input
                                type="text"
                                className="bg-transparent outline-none py-1 w-full text-sm text-gray-700"
                                placeholder="Search"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <DateRangePicker onChange={setDateRange} />
                    </div>

                    <div className="flex gap-2 items-center">
                        <Button
                            onClick={handleExportPDF}
                            className={` ${isExportDisabled ? '!cursor-not-allowed !opacity-75' : 'hover:!bg-gray-50'} flex items-center gap-1 !text-sm !px-3 !py-1.5 border rounded-md `}
                            label="PDF"
                            icon={<FiDownload className="text-gray-600" />}
                            iconPosition='left'
                            disabled={isExportDisabled}
                        />
                        <div
                            className={`flex items-center gap-1 !text-sm !px-3 !py-1.5 border rounded-md ${isExportDisabled ? 'cursor-not-allowed opacity-75' : 'hover:bg-gray-50'}`}
                        >
                            <CSVLink
                                data={csvData}
                                onClick={(event) => {
                                    if (isExportDisabled) {
                                        event.preventDefault();
                                    }
                                }}
                                filename={'Vehicle Report'}
                                className={`${isExportDisabled ? 'cursor-not-allowed' : ''} flex items-center gap-1`}
                            >
                                <FiDownload className="text-gray-600" />
                                CSV
                            </CSVLink>
                        </div>
                    </div>
                </div>

                <VehicleTable
                    data={jobData}
                    globalFilter={searchTerm}
                    onGlobalFilterChange={setSearchTerm}
                    setData={setJobData}
                    onClearNewlyAddedItem={handleClearNewlyAddedItem}
                    fetchJobs={fetchJobs}
                    loadingData={loading}
                    accountId={accountId}
                    jobStatuses={jobStatuses}
                    setSelectedRows={setSelectedRows}
                    selectedRows={selectedRows}
                // vehicleTypes={vehicleTypes}
                // vehicleBodies={vehicleBodies}
                // vehicleMakes={vehicleMakes}
                // globalDataLoading={globalDataLoading}
                // fetchGlobalData={fetchGlobalData}
                />

                <Modal
                    isOpen={openAddModal}
                    onClose={handleCloseAddModal}
                    modalClass={'xl:w-[40%] lg:w-[45%] md:w-[65%] sm:w-[70%] w-[90%] !px-0'}
                >
                    <AddModal
                        handleCloseAddModal={handleCloseAddModal}
                        onAdd={handleAddItem}
                        openEditModal={handleOpenEditModal}
                        // vehicleTypes={vehicleTypes}
                        // vehicleBodies={vehicleBodies}
                        // vehicleMakes={vehicleMakes}
                        accountId={accountId}
                    // globalDataLoading={globalDataLoading || fetchingGlobalData}
                    />
                </Modal>

            </div>
        </div>
    );
};

export default Jobs;